import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useGetTagAssignmentsQuery,
  useBulkSetTagsMutation,
} from "~/ui-rtk/api/tagAssignmentsApi"
import {
  useGetTagCategoriesQuery,
  useCreateTagCategoriesMutation,
  useRenameTagCategoryMutation,
  useUpdateTagCategoryMutation,
  useUpdateTagOptionMutation,
  useDeleteTagCategoryMutation,
  useCreateTagOptionMutation,
  useBulkCreateOptionsMutation,
  useDeleteTagOptionMutation,
  type CreateTagCategoryInput,
} from "~/ui-rtk/api/tagCategoriesApi"
import {
  isPendingFreeTextId,
  pendingFreeTextLabel,
  type ItemTagState,
  type TagCategory,
  type TagOption,
} from "~/ui-rtk/types/structured-tags"

/**
 * Structured tags are always enabled (no feature flag gate).
 */
export function useStructuredTagAccess(): boolean {
  return true
}

/**
 * Hook to fetch tag categories for the current company.
 */
export function useTagCategories() {
  const { data: categories, isLoading } = useGetTagCategoriesQuery()
  const [createOptionMutation] = useCreateTagOptionMutation()
  const [bulkCreateMutation] = useBulkCreateOptionsMutation()
  const [createCategoriesMutation] = useCreateTagCategoriesMutation()
  const [renameCategoryMutation] = useRenameTagCategoryMutation()
  const [updateCategoryMutation] = useUpdateTagCategoryMutation()
  const [updateOptionMutation] = useUpdateTagOptionMutation()
  const [deleteCategoryMutation] = useDeleteTagCategoryMutation()
  const [deleteOptionMutation] = useDeleteTagOptionMutation()

  const createOption = useCallback(
    async (
      categorySlug: string,
      label: string,
      color?: string,
    ): Promise<TagOption> =>
      await createOptionMutation({
        slug: categorySlug,
        body: { label, color },
      }).unwrap(),
    [createOptionMutation],
  )

  /** Bulk create/resolve options across categories. Returns { slug: TagOption[] } */
  const bulkCreateOptions = useCallback(
    async (
      optionsByCategory: Record<
        string,
        Array<{ label: string; value?: string; color?: string }>
      >,
    ): Promise<Record<string, TagOption[]>> =>
      await bulkCreateMutation(optionsByCategory).unwrap(),
    [bulkCreateMutation],
  )

  /**
   * Bulk-create categories. Returns `{ created, failed }`. Per-slug failures
   * surface in `failed[]` (with the API's reason string) rather than throwing —
   * callers should inspect both lists and surface failures to the user.
   */
  const createCategories = useCallback(
    async (
      inputs: CreateTagCategoryInput[],
    ): Promise<{
      created: TagCategory[]
      failed: Array<{ slug: string; reason: string }>
    }> => await createCategoriesMutation(inputs).unwrap(),
    [createCategoriesMutation],
  )

  const createCategory = useCallback(
    async (input: CreateTagCategoryInput): Promise<TagCategory> => {
      const { created, failed } = await createCategoriesMutation([
        input,
      ]).unwrap()
      if (created[0]) return created[0]
      const reason = failed[0]?.reason ?? "Unknown error"
      throw new Error(reason)
    },
    [createCategoriesMutation],
  )

  const renameCategory = useCallback(
    async (slug: string, label: string): Promise<void> => {
      await renameCategoryMutation({ slug, label }).unwrap()
    },
    [renameCategoryMutation],
  )

  /** Set a category's description ("" clears it). Guides the AI tag suggester. */
  const updateCategoryDescription = useCallback(
    async (slug: string, description: string): Promise<void> => {
      await updateCategoryMutation({ slug, body: { description } }).unwrap()
    },
    [updateCategoryMutation],
  )

  /** Set an option's description ("" clears it). Guides the AI tag suggester. */
  const updateOptionDescription = useCallback(
    async (
      categorySlug: string,
      optionId: string,
      description: string,
    ): Promise<void> => {
      await updateOptionMutation({
        slug: categorySlug,
        optionId,
        body: { description },
      }).unwrap()
    },
    [updateOptionMutation],
  )

  const deleteCategory = useCallback(
    async (slug: string): Promise<void> => {
      await deleteCategoryMutation({ slug }).unwrap()
    },
    [deleteCategoryMutation],
  )

  /**
   * Remove a single value from a category. The endpoint and its RTK hook have
   * always existed; nothing exposed them, so a company could add values but
   * never retire one.
   */
  const deleteOption = useCallback(
    async (categorySlug: string, optionId: string): Promise<void> => {
      await deleteOptionMutation({ slug: categorySlug, optionId }).unwrap()
    },
    [deleteOptionMutation],
  )

  return {
    categories: categories ?? [],
    isLoading,
    createOption,
    bulkCreateOptions,
    createCategory,
    createCategories,
    renameCategory,
    updateCategoryDescription,
    updateOptionDescription,
    deleteCategory,
    deleteOption,
  }
}

/**
 * Hook to manage structured tag assignments for a single media item.
 * Returns the current tag state and a save function.
 */
export function useItemStructuredTags(
  itemType: string,
  itemId: string,
  options?: { skip?: boolean },
) {
  const { data: serverAssignments, isLoading: isLoadingAssignments } =
    useGetTagAssignmentsQuery(
      { itemType, itemId },
      { skip: !itemId || options?.skip },
    )

  const [bulkSetMutation, { isLoading: isSaving }] = useBulkSetTagsMutation()

  // Local state mirrors server state, editable before save
  const [localTagState, setLocalTagState] = useState<ItemTagState>({})
  const [hasLocalEdits, setHasLocalEdits] = useState(false)

  // Sync server data into local state when it loads/changes (only if no local edits)
  useEffect(() => {
    if (!serverAssignments || hasLocalEdits) return

    const state: ItemTagState = {}
    for (const [slug, assignments] of Object.entries(serverAssignments)) {
      state[slug] = assignments.map(a => a.optionId)
    }
    setLocalTagState(state)
  }, [serverAssignments, hasLocalEdits])

  // Set a category's selected option IDs
  const setCategoryValues = useCallback(
    (categorySlug: string, optionIds: string[]) => {
      setLocalTagState(prev => ({
        ...prev,
        [categorySlug]: optionIds,
      }))
      setHasLocalEdits(true)
    },
    [],
  )

  // Check if local state differs from server state
  const isDirty = useMemo(() => {
    if (!serverAssignments) return Object.keys(localTagState).length > 0

    const serverState: ItemTagState = {}
    for (const [slug, assignments] of Object.entries(serverAssignments)) {
      serverState[slug] = assignments.map(a => a.optionId).sort()
    }

    const allSlugs = new Set([
      ...Object.keys(localTagState),
      ...Object.keys(serverState),
    ])

    for (const slug of allSlugs) {
      const local = (localTagState[slug] ?? []).slice().sort()
      const server = serverState[slug] ?? []
      if (local.length !== server.length) return true
      for (let i = 0; i < local.length; i++) {
        if (local[i] !== server[i]) return true
      }
    }

    return false
  }, [localTagState, serverAssignments])

  // Save local state to server. Pending free-text values (typed in the modal
  // but never persisted) are split out and sent as raw labels — the server
  // resolves them to options as part of this save, so discarding the modal
  // before saving leaves nothing behind.
  const save = useCallback(async () => {
    const tags: ItemTagState = {}
    const freeTextTags: Record<string, string[]> = {}
    for (const [slug, ids] of Object.entries(localTagState)) {
      tags[slug] = ids.filter(id => !isPendingFreeTextId(id))
      const pending = ids
        .filter(isPendingFreeTextId)
        .map(pendingFreeTextLabel)
        .filter(Boolean)
      if (pending.length > 0) freeTextTags[slug] = pending
    }
    await bulkSetMutation({
      itemType,
      itemId,
      tags,
      freeTextTags:
        Object.keys(freeTextTags).length > 0 ? freeTextTags : undefined,
    }).unwrap()
    setHasLocalEdits(false)
  }, [bulkSetMutation, itemType, itemId, localTagState])

  // Discard local edits
  const discard = useCallback(() => {
    if (!serverAssignments) {
      setLocalTagState({})
    } else {
      const state: ItemTagState = {}
      for (const [slug, assignments] of Object.entries(serverAssignments)) {
        state[slug] = assignments.map(a => a.optionId)
      }
      setLocalTagState(state)
    }
    setHasLocalEdits(false)
  }, [serverAssignments])

  return {
    tagState: localTagState,
    setCategoryValues,
    save,
    discard,
    isDirty,
    isSaving,
    isLoading: isLoadingAssignments,
  }
}

/**
 * Utility: Given categories and a tagState, produce a flat list of assigned tag labels for display.
 */
export function getAssignedTagLabels(
  categories: TagCategory[],
  tagState: ItemTagState,
): Array<{ label: string; color: string; categoryLabel: string }> {
  const result: Array<{
    label: string
    color: string
    categoryLabel: string
  }> = []

  for (const cat of categories) {
    const selectedIds = tagState[cat.slug] ?? []
    for (const optId of selectedIds) {
      const opt = cat.options.find(o => o.id === optId)
      if (opt) {
        result.push({
          label: opt.label,
          color: opt.color,
          categoryLabel: cat.label,
        })
      }
    }
  }

  return result
}
